import React, { useContext, useEffect, useMemo } from "react";
import { Layer, Source } from "react-map-gl/mapbox-legacy";
import { SEARCH_RESULT_TYPE } from "common/constants";
import { transparent } from "common-client/utils/mapLayers";
// eslint-disable-next-line no-restricted-imports
import { primitiveColors } from "common-client/utils/styling";
import { AuthContext } from "../../Authorization/AuthContext";
import { SearchContext } from "../../Search/SearchContext";
import { MapZoomContext } from "../contexts/MapZoomContext";
import { LayerContext } from ".";

const SEARCH_RESULTS_LAYER_ID = "search-results";
const SEARCH_RESULTS_SOURCE_ID = "search-results-source";

export const SearchResultsLayer = () => {
  const { account } = useContext(AuthContext);
  const { mapNonce } = useContext(LayerContext);
  const { searchResultsState } = useContext(SearchContext);
  const { zoomToBounds } = useContext(MapZoomContext);

  const { id: accountId } = account!;

  useEffect(() => {
    if (searchResultsState.bounds) {
      void zoomToBounds({
        bounds: searchResultsState.bounds,
        infoPanelVisible: true,
      });
    }
  }, [searchResultsState.bounds, zoomToBounds]);

  const { propertyIds, parcelIds } = useMemo(() => {
    const properties: Array<string> = [];
    const parcels: Array<string> = [];
    for (const result of searchResultsState.results ?? []) {
      if (result.__typename === SEARCH_RESULT_TYPE.PROPERTY) {
        properties.push(result.id);
      } else if (result.__typename === SEARCH_RESULT_TYPE.PARCEL) {
        parcels.push(result.id);
      }
    }
    return { propertyIds: properties, parcelIds: parcels };
  }, [searchResultsState.results]);

  const searchResultsTiles = useMemo(
    () => [
      `${window.env.APPLICATION_URL}/api/tiles/search-results/${accountId}/{z}/{x}/{y}?nonce=${mapNonce}&propertyIds=${encodeURIComponent(propertyIds.join(","))}&parcelIds=${encodeURIComponent(parcelIds.join(","))}`,
    ],
    [accountId, mapNonce, propertyIds, parcelIds],
  );

  return (
    <Source
      id={SEARCH_RESULTS_SOURCE_ID}
      tiles={searchResultsTiles}
      type="vector"
      minzoom={12}
      //@ts-ignore - the TS types for mapbox gl leave a lot to be desired
      promoteId="id"
    >
      <Layer
        id={`${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}-fill`}
        type="fill"
        source-layer={SEARCH_RESULT_TYPE.PARCEL}
        paint={{
          "fill-color": transparent,
        }}
      />
      <Layer
        id={`${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}`}
        type="line"
        source-layer={SEARCH_RESULT_TYPE.PARCEL}
        paint={{
          "line-width": 1.5,
          "line-color": [
            "case",
            ["==", ["feature-state", "hover"], true],
            primitiveColors.blue200,
            primitiveColors.white,
          ],
        }}
      />

      <Layer
        id={`${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PROPERTY}`}
        type="circle"
        source-layer={SEARCH_RESULT_TYPE.PROPERTY}
        minzoom={12}
        paint={{
          "circle-radius": 5,
          "circle-stroke-width": 1.5,
          "circle-stroke-color": [
            "case",
            ["==", ["feature-state", "hover"], true],
            primitiveColors.blue200,
            primitiveColors.white,
          ],
          "circle-color": primitiveColors.blue500,
        }}
      />
    </Source>
  );
};
